import { Spacing } from '@persona-id/design-tokens';
import { useEffect } from 'react';
import styled from 'styled-components';

import GraphReadOnlyQuery from 'dashboard/components/GraphReadOnlyQuery';
import ReactSelect from 'dashboard/components/ReactSelect';
import { AccountStatus } from 'dashboard/models/account-status';
import { CaseQuery } from 'dashboard/models/case-query';
import { Graph } from 'dashboard/models/graph';
import {
  GraphQueryTemplate,
  GraphQueryTemplateStatus,
} from 'dashboard/models/graph-query-template';
import { ModuleGraphV2 } from 'dashboard/modules/GraphV2';
import { getRunnableGraphQueryTemplates } from 'dashboard/modules/GraphV2/utils';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label } from 'design-system/components/Text';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const Container = styled('div')`
  padding: ${Spacing[5]};
  overflow: visible;
`;

export interface GraphV2PanelProps {
  accountStatuses: AccountStatus[];
  caseQueries: CaseQuery[];
  fieldSchema: FieldSchema[];
  graph: Graph | undefined;
  graphQueryTemplates: GraphQueryTemplate[];
  module: ModuleGraphV2;
  updateModule: (module: ModuleGraphV2) => void;
}

const templatesStillLoadingOptions = [
  {
    label: 'Loading...',
    value: 'Loading...',
    disabled: true,
  },
];

const GraphV2Panel = ({
  accountStatuses,
  caseQueries,
  fieldSchema,
  graph,
  graphQueryTemplates,
  module,
  updateModule,
}: GraphV2PanelProps) => {
  const showExcludedNodes = useFeatureFlag('dashboard:graph-excluded-nodes');
  const caseQueryOptions = (caseQueries || []).map((caseQuery) => ({
    label: caseQuery.attributes.name,
    value: caseQuery.id,
  }));
  // Temporary for conversion from single select to multi select by changing value from
  // string to string[]
  useEffect(() => {
    if (!Array.isArray(module['expected-graph-query-template'])) {
      updateModule({
        ...module,
        'expected-graph-query-template': [module['expected-graph-query-template']],
        'field-for-excluded-nodes': module['field-for-excluded-nodes'],
        'field-for-hidden-nodes': module['field-for-hidden-nodes'],
        'field-for-visible-nodes': module['field-for-visible-nodes'],
        'case-queries-to-rerun-on-exclude': module['case-queries-to-rerun-on-exclude'],
      } as ModuleGraphV2);
    }
  }, [module, updateModule]);

  const updateGraphQueryTemplates = (expectedGraphQueryTemplates: string[]) => {
    updateModule({
      ...module,
      'expected-graph-query-template': expectedGraphQueryTemplates,
    } as ModuleGraphV2);
  };

  const updateFieldForExcludedNodes = (fieldForExcludedNodes: string) => {
    updateModule({
      ...module,
      'field-for-excluded-nodes': fieldForExcludedNodes,
    } as ModuleGraphV2);
  };

  const updateFieldForHiddenNodes = (fieldForHiddenNodes: string) => {
    updateModule({
      ...module,
      'field-for-hidden-nodes': fieldForHiddenNodes,
    } as ModuleGraphV2);
  };

  const updateFieldForVisibleNodes = (fieldForVisibleNodes: string) => {
    updateModule({
      ...module,
      'field-for-visible-nodes': fieldForVisibleNodes,
    } as ModuleGraphV2);
  };

  const updateCaseQueriesToRerunOnExclude = (caseQueriesToRerunOnExclude: string[]) => {
    updateModule({
      ...module,
      'case-queries-to-rerun-on-exclude': caseQueriesToRerunOnExclude,
    } as ModuleGraphV2);
  };

  const allowedFieldsForExcludedNodes = fieldSchema.filter((f) => f.type === FieldSchemaType.Json);
  const allowedFieldsForHiddenNodes = fieldSchema.filter((f) => f.type === FieldSchemaType.Json);
  const allowedFieldsForVisibleNodes = fieldSchema.filter((f) => f.type === FieldSchemaType.Json);

  const fieldForExcludedNodesOptions = allowedFieldsForExcludedNodes.map((f) => {
    return {
      label: f.key,
      value: f.key,
    };
  });
  const fieldForHiddenNodesOptions = allowedFieldsForHiddenNodes.map((f) => {
    return {
      label: f.key,
      value: f.key,
    };
  });
  const fieldForVisibleNodesOptions = allowedFieldsForVisibleNodes.map((f) => {
    return {
      label: f.key,
      value: f.key,
    };
  });

  const templatesStillLoading = graphQueryTemplates == null;
  const templateOptions = getRunnableGraphQueryTemplates(graphQueryTemplates)
    .filter(
      (graphQueryTemplate: GraphQueryTemplate) =>
        graphQueryTemplate.attributes.status === GraphQueryTemplateStatus.Active,
    )
    .map((graphQueryTemplate: GraphQueryTemplate) => {
      return {
        label: graphQueryTemplate.attributes.name,
        value: graphQueryTemplate.id,
      };
    })
    .sort(({ label: l1 }, { label: l2 }) => l1.localeCompare(l2));
  const selectedTemplates = graphQueryTemplates.filter((template) =>
    (module['expected-graph-query-template'] || []).includes(template.id),
  );

  return (
    <Container>
      <Heading size='sm'>Settings</Heading>
      <Spacer height={3} />
      <Label size='md' weight='bold' id='graph-v2-panel__graph-query-template-selector--label'>
        Query template
      </Label>
      <ReactSelect
        id='graph-v2-panel__graph-query-template-selector'
        aria-labelledby='graph-v2-panel__graph-query-template-selector--label'
        isMulti
        options={templatesStillLoading ? templatesStillLoadingOptions : templateOptions}
        value={selectedTemplates.map((template) => template.id)}
        onChange={updateGraphQueryTemplates}
        portal={true}
      />
      {selectedTemplates.map((selectedTemplate) => [
        <Spacer key={`graph-readonly-space-${selectedTemplate.id}`} height={1} />,
        <GraphReadOnlyQuery
          graph={graph}
          accountStatuses={accountStatuses.map((accountStatus) => {
            return {
              name: accountStatus.attributes.name,
              token: accountStatus.id,
            };
          })}
          key={`graph-readonly-${selectedTemplate.id}`}
          query={selectedTemplate}
        />,
      ])}
      {showExcludedNodes && (
        <>
          <Spacer height={3} />
          <Heading size='sm'>Node exclusion</Heading>
          <Spacer height={3} />
          <Label size='md' weight='bold'>
            Excluded Nodes Field
          </Label>
          <ReactSelect
            options={fieldForExcludedNodesOptions}
            value={module['field-for-excluded-nodes']}
            onChange={updateFieldForExcludedNodes}
          />
          <Spacer height={1} />
          <Label size='md' weight='bold'>
            Hidden Nodes Field
          </Label>
          <ReactSelect
            options={fieldForHiddenNodesOptions}
            value={module['field-for-hidden-nodes']}
            onChange={updateFieldForHiddenNodes}
          />
          <Spacer height={1} />
          <Label size='md' weight='bold'>
            Visible Nodes Field
          </Label>
          <ReactSelect
            options={fieldForVisibleNodesOptions}
            value={module['field-for-visible-nodes']}
            onChange={updateFieldForVisibleNodes}
          />
          <Spacer height={1} />
          <Label size='md' weight='bold'>
            Dependent Case Queries
          </Label>
          <ReactSelect
            isMulti
            options={caseQueryOptions}
            value={module['case-queries-to-rerun-on-exclude']}
            onChange={updateCaseQueriesToRerunOnExclude}
          />
        </>
      )}
    </Container>
  );
};

export default GraphV2Panel;
